import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';

/**
 * LeaveManager — unified component for managing leave (PTO/sick/etc).
 *
 * Props:
 *  - mode: 'staff' | 'admin'
 *      'staff': lists own requests + lets the user open new ones.
 *      'admin': lists every request in the org with approve/reject actions.
 *  - staffOptions (admin only): list of {id, name} for the on-behalf picker.
 */

const LEAVE_TYPES = [
  { v: 'holiday', label: '🌴 Holiday', tone: 'hsl(190 65% 55%)' },
  { v: 'sick', label: '🤒 Sick', tone: 'hsl(0 70% 55%)' },
  { v: 'unpaid', label: '🚫 Unpaid', tone: 'hsl(35 95% 50%)' },
  { v: 'other', label: '📋 Other', tone: 'hsl(260 55% 55%)' },
];

const STATUS_TONES = {
  pending: { bg: 'hsl(45 95% 92%)', fg: 'hsl(35 80% 30%)', label: '⏳ Pending' },
  approved: { bg: 'hsl(140 50% 92%)', fg: 'hsl(140 50% 30%)', label: '✅ Approved' },
  rejected: { bg: 'hsl(0 70% 94%)', fg: 'hsl(0 60% 35%)', label: '❌ Rejected' },
  cancelled: { bg: 'hsl(220 15% 92%)', fg: 'hsl(220 15% 30%)', label: '✕ Cancelled' },
};

const fmtDate = (s) => {
  try { return new Date(s).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

const daysBetween = (a, b) => {
  const A = new Date(a); const B = new Date(b);
  const ms = B.setHours(0, 0, 0, 0) - A.setHours(0, 0, 0, 0);
  return Math.round(ms / 86400000) + 1;
};

export function LeaveManager({ mode = 'staff', staffOptions = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    type: 'holiday', start_date: '', end_date: '', reason: '', staff_id: '',
  });
  const [saving, setSaving] = useState(false);
  // Leave balance (current year). Staff: their own. Admin: balance for the
  // currently-picked staff in the form (only shown after they pick one).
  const [balance, setBalance] = useState(null);

  const loadBalance = async (staffId = null) => {
    try {
      if (mode === 'staff') {
        const b = await api.getLeaveBalance();
        setBalance(b);
      } else if (mode === 'admin' && staffId) {
        const b = await api.getLeaveBalance(staffId);
        setBalance(b);
      } else {
        setBalance(null);
      }
    } catch (err) {
      console.error('Balance load failed', err);
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getLeaveRequests(filter === 'all' ? {} : { status: filter });
      setRows(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { loadBalance(); /* eslint-disable-next-line */ }, [mode]);
  useEffect(() => {
    if (mode === 'admin' && form.staff_id) loadBalance(form.staff_id);
    // eslint-disable-next-line
  }, [form.staff_id, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) {
      setError('Pick start and end dates');
      return;
    }
    if (form.end_date < form.start_date) {
      setError('End date must be on or after start date');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form };
      if (mode !== 'admin') delete payload.staff_id;
      else if (!payload.staff_id) {
        setError('Pick a staff member to apply on behalf of');
        setSaving(false);
        return;
      }
      await api.createLeaveRequest(payload);
      setForm({ type: 'holiday', start_date: '', end_date: '', reason: '', staff_id: '' });
      setShowForm(false);
      load();
      loadBalance(mode === 'admin' ? payload.staff_id : null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (r) => {
    const note = window.prompt('Optional note:', '');
    if (note === null) return;
    try { await api.approveLeave(r._id, note); load(); }
    catch (err) { alert(err.message); }
  };
  const handleReject = async (r) => {
    const note = window.prompt('Reason for rejection:', '');
    if (note === null) return;
    try { await api.rejectLeave(r._id, note); load(); }
    catch (err) { alert(err.message); }
  };
  const handleCancel = async (r) => {
    if (!window.confirm('Cancel this request?')) return;
    try { await api.cancelLeave(r._id); load(); }
    catch (err) { alert(err.message); }
  };

  const totalDays = (r) => daysBetween(r.start_date, r.end_date);

  return (
    <div data-testid={`leave-manager-${mode}`}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, flex: '1 1 200px' }}>
          {mode === 'admin' ? '🏖️ Leave Requests' : '🏖️ My Leave'}
        </h2>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="leave-filter">
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => { setShowForm((v) => !v); setError(null); }}
            data-testid="leave-toggle-form"
          >
            {showForm ? 'Close' : (mode === 'admin' ? '+ Add (on behalf)' : '+ Request leave')}
          </button>
        </div>
      </div>

      {error && <div className="error-message" data-testid="leave-error">{error}</div>}

      {balance && (
        <div
          className="card"
          data-testid="leave-balance-card"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.5rem',
            padding: '0.85rem 1rem',
            marginBottom: '1rem',
          }}
        >
          {[
            ['Allowance', `${balance.allowance_total} d`, 'Annual + carried'],
            ['Used', `${balance.used_days} d`, 'Approved holidays'],
            ['Pending', `${balance.pending_days} d`, 'Awaiting decision'],
            ['Remaining', `${balance.remaining_days} d`, `Year ${balance.leave_year_start} → ${balance.leave_year_end}`],
          ].map(([label, value, sub], i) => (
            <div key={label}>
              <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {label}
              </div>
              <div
                style={{
                  fontSize: '1.4rem', fontWeight: 700,
                  color: i === 3 && balance.remaining_days <= 0
                    ? 'hsl(0 70% 50%)'
                    : i === 3 && balance.remaining_days <= 5
                      ? 'hsl(35 95% 45%)'
                      : 'inherit',
                }}
                data-testid={`leave-balance-${label.toLowerCase()}`}
              >
                {value}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))' }}>{sub}</div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <form
          className="card"
          onSubmit={handleSubmit}
          style={{ padding: '1rem', marginBottom: '1rem', borderStyle: 'dashed' }}
          data-testid="leave-form"
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
            {mode === 'admin' && (
              <div className="form-group" style={{ margin: 0 }}>
                <label>Staff member</label>
                <select
                  value={form.staff_id}
                  onChange={(e) => setForm({ ...form, staff_id: e.target.value })}
                  required
                  data-testid="leave-staff"
                >
                  <option value="">— Select staff —</option>
                  {staffOptions.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group" style={{ margin: 0 }}>
              <label>Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="leave-type"
              >
                {LEAVE_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Start date</label>
              <input
                type="date" required
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                data-testid="leave-start"
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>End date</label>
              <input
                type="date" required
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                data-testid="leave-end"
              />
            </div>
            <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
              <label>Reason (optional)</label>
              <input
                type="text"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. Family wedding"
                data-testid="leave-reason"
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="leave-save">
              {saving ? 'Saving…' : mode === 'admin' ? 'Submit (will need admin approval unless you mark approved)' : 'Submit request'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div data-testid="leave-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state" data-testid="leave-empty">No leave requests in this view.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {rows.map((r) => {
            const status = STATUS_TONES[r.status] || STATUS_TONES.pending;
            const lt = LEAVE_TYPES.find((t) => t.v === r.type) || LEAVE_TYPES[0];
            return (
              <div
                key={r._id}
                data-testid={`leave-row-${r._id}`}
                className="card"
                style={{ padding: '0.85rem 1rem' }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {mode === 'admin' && <strong>{r.staff_name}</strong>}
                      <span
                        className="badge"
                        style={{
                          background: `${lt.tone}15`, color: lt.tone, fontWeight: 600,
                          padding: '0.2rem 0.5rem', borderRadius: '999px', fontSize: '0.75rem',
                        }}
                      >
                        {lt.label}
                      </span>
                      <span
                        className="badge"
                        style={{
                          background: status.bg, color: status.fg, fontWeight: 600,
                          padding: '0.2rem 0.5rem', borderRadius: '999px', fontSize: '0.75rem',
                        }}
                        data-testid={`leave-status-${r._id}`}
                      >
                        {status.label}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                      {fmtDate(r.start_date)} → {fmtDate(r.end_date)} ·{' '}
                      <span style={{ color: 'hsl(var(--muted-foreground))' }}>
                        {totalDays(r)} day{totalDays(r) === 1 ? '' : 's'}
                      </span>
                    </div>
                    {r.reason && (
                      <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.15rem' }}>
                        “{r.reason}”
                      </div>
                    )}
                    {r.review_note && (
                      <div style={{ fontSize: '0.8rem', marginTop: '0.25rem', color: 'hsl(var(--muted-foreground))' }}>
                        Note: {r.review_note}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {mode === 'admin' && r.status === 'pending' && (
                      <>
                        <button className="btn btn-primary" onClick={() => handleApprove(r)} data-testid={`leave-approve-${r._id}`}>
                          ✅ Approve
                        </button>
                        <button className="btn btn-secondary" onClick={() => handleReject(r)} data-testid={`leave-reject-${r._id}`}>
                          ❌ Reject
                        </button>
                      </>
                    )}
                    {mode === 'staff' && r.status === 'pending' && (
                      <button className="btn btn-ghost" onClick={() => handleCancel(r)} data-testid={`leave-cancel-${r._id}`}>
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default LeaveManager;
